import React, { useCallback, useMemo, useState } from 'react';
import Button from 'components/commercetools-ui/atoms/button';
import Checkbox from 'components/commercetools-ui/atoms/checkbox';
import Dropdown from 'components/commercetools-ui/atoms/dropdown';
import Modal from 'components/commercetools-ui/atoms/modal';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
import useProcessing from 'helpers/hooks/useProcessing';
import useValidate from 'helpers/hooks/useValidate';
import countryStates from 'static/states.json';
import { useAccount } from 'frontastic';
import AddressForm from '../steps/sections/addresses/components/address-form';
import { Fields, FieldsOptions } from '../steps/sections/addresses/components/address-form/types';
import useMappers from '../steps/sections/addresses/hooks/useMappers';
import { Address } from '../steps/sections/addresses/types';

const CreateAddressModal = () => {
  const { formatMessage } = useFormat({ name: 'common' });

  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });

  const { formatMessage: formatCheckoutMessage } = useFormat({ name: 'checkout' });

  const { validatePhoneNumeric, validatePostalCode, validateEmail } = useValidate();

  const { processing, startProcessing, stopProcessing } = useProcessing();

  const { addressToAccountAddress } = useMappers();

  const { addShippingAddress, addBillingAddress, loggedIn } = useAccount();

  const { getCountry } = useLocale();

  const states = countryStates[getCountry() as keyof typeof countryStates] ?? [];

  const [isOpen, setIsOpen] = useState(false);

  const closeModal = useCallback(() => setIsOpen(false), []);

  const initialData = useMemo(
    () =>
      ({
        title: 'Mr',
        firstName: '',
        lastName: '',
        email: '',
        phone: '', // mobile
        line1: '',
        line2: '',
        postalCode: '',
        city: '',
        county: '',
        country: getCountry(),
        addressType: 'shipping',
      } as Address),
    [],
  );

  const [data, setData] = useState<Address>(initialData);

  const [saveAsDefault, setSaveAsDefault] = useState(false);
  interface AddressInfo {
    addressType?: string;
    line1?: string;
    line2?: string;
    postcode?: string;
    town?: string;
    postalCounty?: string;
    longitude?: number;
    latitude?: number;
  }

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setData({ ...data, [e.target.name]: e.target.value });
    },
    [data],
  );

  const onAFDAddressSelection = (addressInfo: AddressInfo) => {
    setData({
      ...data,
      line1: addressInfo.line1 || '',
      line2: addressInfo.line2 || '',
      city: addressInfo.town || '',
      county: addressInfo.postalCounty || '',
      postalCode: addressInfo.postcode || '',
      longitude: addressInfo.longitude,
      latitude: addressInfo.latitude,
    });
  };

  const onAddressCountrySelection = (countryCode: string) => {
    setData({ ...data, country: countryCode });
  };

  const handleSubmit = useCallback(async () => {
    const isPostCodeValid = validatePostalCode(data.postalCode, data.country);
    const isPhoneNumberValid = validatePhoneNumeric(data.phone || '');
    if (!isPostCodeValid || !isPhoneNumberValid) {
      return false;
    }
    startProcessing();

    await (data.addressType === 'shipping' ? addShippingAddress : addBillingAddress)({
      ...addressToAccountAddress(data),
      isDefaultShippingAddress: data.addressType === 'shipping' && saveAsDefault,
      isDefaultBillingAddress: data.addressType === 'billing' && saveAsDefault,
    });
    stopProcessing();
    closeModal();
    setData(initialData);
    setSaveAsDefault(false);
  }, [
    addShippingAddress,
    addBillingAddress,
    data,
    addressToAccountAddress,
    saveAsDefault,
    startProcessing,
    stopProcessing,
    closeModal,
    initialData,
  ]);

  const addressTypeOptions = useMemo(() => {
    return [
      {
        label: formatCheckoutMessage({ id: 'shippingAddress', defaultMessage: 'Shipping Address' }),
        value: 'shipping',
      },
      { label: formatCheckoutMessage({ id: 'billingAddress', defaultMessage: 'Billing Address' }), value: 'billing' },
    ];
  }, [formatCheckoutMessage]);

  const fields = useCallback(
    ({ enableAddress2, onEnableAddress2 }: FieldsOptions) => {
      const fieldList: Fields[] = [
        {
          name: 'firstName',
          label: formatMessage({ id: 'firstName', defaultMessage: 'First Name' }),
          labelDesc: '',
          required: true,
          maxLength: 21,
          type: 'string',
          className: 'md:col-span-2 col-span-4',
        },
        {
          name: 'lastName',
          label: formatMessage({ id: 'lastName', defaultMessage: 'Last Name' }),
          labelDesc: '',
          required: true,
          maxLength: 31,
          type: 'string',
          className: 'md:col-span-2 col-span-4',
        },

        {
          name: 'phone',
          label: `${formatMessage({ id: 'phone', defaultMessage: 'Mobile' })}`,
          labelDesc: '',
          type: 'string',
          required: true,
          className: 'md:col-span-2 col-span-4',
          maxLength: 20,
          validate(value) {
            return validatePhoneNumeric(value);
          },
        },
        {
          name: 'line1',
          label: `${formatMessage({ id: 'address', defaultMessage: 'Address' })} 1`,
          labelDesc: '',
          required: true,
          type: 'string',
          className: 'md:col-span-2 col-span-4',
          render() {
            if (enableAddress2) return <></>;

            return (
              <div className="col-span-3 mt-16 cursor-pointer">
                <p className="w-fit text-14 text-secondary-black" onClick={onEnableAddress2}>
                  + {formatCheckoutMessage({ id: 'add.address', defaultMessage: 'Add another address line' })}
                </p>
              </div>
            );
          },
        },
        ...(enableAddress2
          ? [
              {
                name: 'line2',
                label: `${formatMessage({ id: 'address', defaultMessage: 'Address' })} 2`,
                labelDesc: '',
                type: 'string',
                className: 'md:col-span-2 col-span-4',
              },
            ]
          : []),
        {
          name: 'postalCode',
          label: formatMessage({ id: 'zipCode', defaultMessage: 'Postcode' }),
          labelDesc: '',
          required: true,
          className: 'md:col-span-2 col-span-4',
          validate(value, countryCode) {
            return validatePostalCode(value, countryCode || getCountry());
          },
        },
        {
          name: 'city',
          label: formatMessage({ id: 'city', defaultMessage: 'City' }),
          labelDesc: '',
          required: true,
          className: 'md:col-span-2 col-span-4',
        },
        {
          name: 'county',
          label: formatMessage({ id: 'county', defaultMessage: 'County' }),
          labelDesc: '',
          required: false,
          className: 'md:col-span-2 col-span-4 mb-12',
        },
      ] as Fields[];
      if (!loggedIn) {
        fieldList.splice(2, 0, {
          name: 'email',
          label: formatMessage({ id: 'email', defaultMessage: 'Email' }),
          labelDesc: '',
          required: true,
          type: 'email',
          className: 'md:col-span-2 col-span-4',
          validate(value) {
            return validateEmail(value);
          },
        });
      }

      return fieldList;
    },
    [formatMessage, formatCheckoutMessage],
  );

  const onSelectedTitle = (selectedTitle: string) => {
    setData({ ...data, title: selectedTitle });
  };

  if (!loggedIn) return <></>;

  return (
    <>
      <p className="text-14 underline underline-offset-2 hover:cursor-pointer" onClick={() => setIsOpen(true)}>
        {formatAccountMessage({ id: 'address.add', defaultMessage: 'Add new address' })} +
      </p>
      <Modal
        isOpen={isOpen}
        onRequestClose={closeModal}
        style={{ content: { background: 'transparent', border: 'none' } }}
        closeTimeoutMS={200}
      >
        <div className="mx-auto max-h-[100vh] w-[100%] max-w-[800px] overflow-y-auto rounded-sm bg-white p-32 pt-24">
          <h4 className="text-24">{formatAccountMessage({ id: 'address.add', defaultMessage: 'Add new address' })}</h4>
          <AddressForm
            addressType=""
            className="mt-32"
            address={data}
            fields={fields}
            onChange={handleChange}
            onSubmit={handleSubmit}
            onAFDAddressSelection={onAFDAddressSelection}
            onAddressCountrySelection={onAddressCountrySelection}
            onSelectedTitle={onSelectedTitle}
          >
            {states.length > 0 && (
              <div className="mt-12">
                <Dropdown
                  name="state"
                  value={data.state ?? ''}
                  items={[{ label: '', value: '' }, ...states.map(({ name, code }) => ({ label: name, value: code }))]}
                  className="w-full border-neutral-500"
                  onChange={handleChange}
                  label={formatMessage({
                    id: 'state',
                    defaultMessage: 'State',
                  })}
                />
              </div>
            )}
            <div className="mt-12">
              <Dropdown
                name="addressType"
                items={addressTypeOptions}
                className="w-full border-neutral-500"
                onChange={handleChange}
                label={`${formatAccountMessage({
                  id: 'address.type',
                  defaultMessage: 'Address type',
                })}`}
                isRequired={true}
              />
            </div>
            <div className="mt-16">
              <Checkbox
                label={formatCheckoutMessage({
                  id: 'address.setDefault',
                  defaultMessage: 'Save as default address',
                })}
                labelPosition="on-right"
                checked={saveAsDefault}
                onChange={({ checked }) => setSaveAsDefault(checked)}
              />
            </div>
            <div className="mt-32 flex gap-12">
              <Button variant="secondary" className="px-48" type="button" onClick={closeModal}>
                {formatMessage({ id: 'cancel', defaultMessage: 'Cancel' })}
              </Button>
              <Button variant="primary" className="px-48" type="submit" loading={processing}>
                {formatMessage({ id: 'save', defaultMessage: 'Save' })}
              </Button>
            </div>
          </AddressForm>
        </div>
      </Modal>
    </>
  );
};

export default CreateAddressModal;
